import { Shareholder, ShareholderResponse, CreateShareholderData } from '../../types/api.types';
import { validateShareholder } from '../../validation/schemas';
import { INITIAL_SHAREHOLDERS } from './initialShareholdersData';

// NOTE:
// This service has been decoupled from the real backend.
// All shareholder operations now work against an in-memory dataset.

const makeCusipData = (cusip: string, createdAt: string, updatedAt: string) => ({
    cusip,
    description: '',
    createdAt,
    updatedAt,
});

// Mutable in-memory store
let shareholderStore: Shareholder[] = [...INITIAL_SHAREHOLDERS];

const CUSIP_TO_ISSUER: Record<string, string> = {
    '037833100': 'Apple',
    '88160R101': 'Tesla',
    '67066G104': 'NVIDIA',
    '00214Q104': 'iShares',
    '46090E103': 'Invesco',
    '46090A689': 'Invesco',
    '46090A697': 'Invesco',
    '46090A879': 'Invesco',
    '46138G888': 'Invesco',
    '46137V399': 'Invesco',
    '46137V563': 'Invesco',
    '46137V621': 'Invesco',
    '46138E511': 'Invesco',
    '46138E537': 'Invesco',
    '46138E206': 'Invesco',
    '46138E354': 'Invesco',
    '46138E610': 'Invesco',
    '46138E594': 'Invesco',
    '46138E719': 'Invesco',
    '46138G870': 'Invesco',
    '46138E131': 'Invesco',
    '46138E404': 'Invesco',
};

const getIssuerName = (cusip: string): string => CUSIP_TO_ISSUER[(cusip || '').trim()] ?? '';

export class ShareholderService {
    protected validate(data: Partial<Shareholder>) {
        return validateShareholder(data as any);
    }

    async getAllShareholders(
        query: string,
        page: number = 1,
        pageSize: number = 10,
        issuerName?: string
    ): Promise<ShareholderResponse> {
        const keyword = query?.trim().toLowerCase();
        const issuerFilter = issuerName?.trim();
        let filtered = shareholderStore;

        if (keyword) {
            filtered = filtered.filter(
                sh =>
                    sh.accountNumber.toLowerCase().includes(keyword) ||
                    sh.fullName.toLowerCase().includes(keyword) ||
                    sh.email.toLowerCase().includes(keyword) ||
                    sh.cusip.toLowerCase().includes(keyword)
            );
        }
        if (issuerFilter) {
            filtered = filtered.filter(sh => getIssuerName(sh.cusip) === issuerFilter);
        }

        const total = filtered.length;
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        const pageItems = filtered.slice(start, end);

        return Promise.resolve({
            data: pageItems,
            total,
            page,
            pageSize,
        });
    }

    async getShareholderById(id: string): Promise<Shareholder> {
        const found = shareholderStore.find(sh => sh.id === id);
        if (!found) {
            throw new Error('Shareholder not found');
        }
        return Promise.resolve({ ...found });
    }

    async createShareholder(data: CreateShareholderData): Promise<Shareholder> {
        const now = new Date().toISOString();
        const id = `SH-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const newShareholder: Shareholder = {
            id,
            cusip: data.cusip,
            accountNumber: data.accountNumber,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            fullName: `${data.firstName} ${data.lastName}`.trim(),
            numberOfShares: data.numberOfShares,
            totalShares: data.numberOfShares,
            recordDate: data.recordDate,
            mapAccountAcknowledgement: false,
            naaPreference: null,
            noboObo: null,
            preferredVoting: null,
            managerCode: null,
            repCode: null,
            legalProxyRequested: false,
            controlNo: data.controlNo,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2 ?? null,
            city: data.city,
            state: data.state,
            zipCode: data.zipCode,
            country: data.country,
            createdAt: now,
            updatedAt: now,
            brokerId: null as any,
            cusipData: makeCusipData(data.cusip, now, now),
        };

        // Add new shareholder at the top of the list
        shareholderStore = [newShareholder, ...shareholderStore];
        return Promise.resolve(newShareholder);
    }

    async updateShareholder(id: string, data: Partial<CreateShareholderData>): Promise<Shareholder> {
        const index = shareholderStore.findIndex(sh => sh.id === id);
        if (index === -1) {
            throw new Error('Shareholder not found');
        }

        const existing = shareholderStore[index];
        const updated: Shareholder = {
            ...existing,
            ...data,
            fullName: `${data.firstName ?? existing.firstName} ${data.lastName ?? existing.lastName}`.trim(),
            numberOfShares: data.numberOfShares ?? existing.numberOfShares,
            totalShares: data.numberOfShares ?? existing.totalShares,
            addressLine2: data.addressLine2 ?? existing.addressLine2,
            updatedAt: new Date().toISOString(),
        };

        shareholderStore = [
            ...shareholderStore.slice(0, index),
            updated,
            ...shareholderStore.slice(index + 1),
        ];

        return Promise.resolve(updated);
    }

    async deleteShareholder(id: string): Promise<void> {
        shareholderStore = shareholderStore.filter(sh => sh.id !== id);
        return Promise.resolve();
    }

    async exportToCsv(): Promise<Blob> {
        const header = [
            'id',
            'cusip',
            'accountNumber',
            'email',
            'firstName',
            'lastName',
            'fullName',
            'numberOfShares',
            'recordDate',
            'addressLine1',
            'addressLine2',
            'city',
            'state',
            'zipCode',
            'country',
        ].join(',');

        const rows = shareholderStore.map(sh =>
            [
                sh.id,
                sh.cusip,
                sh.accountNumber,
                sh.email,
                sh.firstName,
                sh.lastName,
                sh.fullName,
                sh.numberOfShares,
                sh.recordDate,
                sh.addressLine1,
                sh.addressLine2 ?? '',
                sh.city,
                sh.state,
                sh.zipCode,
                sh.country,
            ]
                .map(value => `"${String(value ?? '').replace(/"/g, '""')}"`)
                .join(',')
        );

        const csv = [header, ...rows].join('\n');
        return Promise.resolve(new Blob([csv], { type: 'text/csv' }));
    }

    async exportToText(): Promise<Blob> {
        const lines = shareholderStore.map(sh => {
            return [
                `Account: ${sh.accountNumber}`,
                `Name   : ${sh.fullName}`,
                `CUSIP  : ${sh.cusip}`,
                `Email  : ${sh.email}`,
                `Shares : ${sh.numberOfShares}`,
                `Addr   : ${sh.addressLine1}${sh.addressLine2 ? ', ' + sh.addressLine2 : ''}, ${sh.city}, ${sh.state} ${sh.zipCode}, ${sh.country}`,
                `Record : ${sh.recordDate}`,
                ''.padEnd(40, '-'),
            ].join('\n');
        });

        const text = lines.join('\n');
        return Promise.resolve(new Blob([text], { type: 'text/plain' }));
    }
}

export const shareholderService = new ShareholderService(); 